import {
  Box,
  HStack,
  KdsCallout,
  KdsText,
  VStack,
} from "@kazaar-platform/design-system";
import { useTranslate } from "@tolgee/react";
import { useRef } from "react";
import {
  ExpressionOfNeed,
  TechnicalSpecifications,
} from "@/components/ExpertMode";
import { CommentsList } from "@/components/ExpertMode/Comments";
import { ProductInfoBloc } from "@/components/OrderItem";
import { ClientReferenceBloc } from "@/components/OrderItem/ClientReferenceBloc";
import { DeliveryInformationsBloc } from "@/components/OrderItem/DeliveryInformationsBloc";
import { DesiredDeliveryDateBloc } from "@/components/OrderItem/DesiredDeliveryDateBloc";
import { OrderItemDetailsOrderLogReadDtoActionTypeEnum } from "@/config/apiClient";
import { useExpertMode } from "@/hooks";
import { useUserCanEditOrder } from "@/hooks/orders/useUserCanEditOrder";
import { useRolePermission } from "@/hooks/rolePermission/useRolePermission";
import { type NewRequestPageContentProps } from "./NewRequestPageContent.types";

export const NewRequestPageContent = ({
  orderItem,
  order,
  handleQualifyDrawerOpen,
}: NewRequestPageContentProps) => {
  const { t } = useTranslate();
  const { hasBrokerKazaarRole } = useRolePermission();

  const isUserCanEditOrder = useUserCanEditOrder(order);

  const commentAreaRef = useRef<HTMLDivElement>(null);

  const productId = orderItem.sandboxItem.catalogMedium?.admaId;
  const technicalVersion =
    orderItem.sandboxItem.catalogMedium?.technicalVersion;

  const { useGetTechnicalFields } = useExpertMode();
  const { data: fetchedFields } = useGetTechnicalFields({
    admaId: productId || 0,
    awsVersion: technicalVersion || 0,
  });

  const fields = fetchedFields?.data.fields;

  const technicalSpecifications =
    orderItem.sandboxItem.catalogMedium?.CatalogMediumOption.sort(
      (a, b) => a.position - b.position
    )
      .map((e) => {
        const ogField = fetchedFields?.data.fields.find(
          (a) => a.id === e.admaId
        );

        const ogFieldValueName = ogField?.values.find(
          (a) => a.value_id === Number(e.value)
        )?.name;

        const ogFieldPrettyValueName = ogFieldValueName
          ? t(ogFieldValueName)
          : "";

        return {
          ...e,
          value: e.type !== "Dropdown list" ? e.value : ogFieldPrettyValueName,
          category: e.category || ogField?.categorie,
        };
      })
      .filter((e) => !!e.value);

  const commentableOrderLog = orderItem?.OrderLog.find(
    (e) =>
      e.actionType ===
      OrderItemDetailsOrderLogReadDtoActionTypeEnum.ItemSentToStudy
  );

  return (
    <Box pb="sp-md">
      <HStack gap="sp-xl">
        <Box w="70%" p="sp-md" pt="auto">
          <VStack gap="sp-md">
            {hasBrokerKazaarRole && (
              <KdsCallout
                title={t("newRequest.callout.productNotQualified.title")}
                state="warning"
                visible={!orderItem.isQualified}
                has="button"
                position="right"
                buttonProps={{
                  children: t("newRequest.callout.productNotQualified.button"),
                  onPress: handleQualifyDrawerOpen,
                }}
              >
                <KdsText>
                  {t("newRequest.callout.productNotQualified.content")}
                </KdsText>
              </KdsCallout>
            )}

            <Box bgColor="elevation.first" w="100%" p="sp-md" rounded="br-sm">
              <VStack gap="sp-md">
                <ProductInfoBloc
                  name={orderItem.sandboxItem.name || ""}
                  isWithPreMedia={!!orderItem.sandboxItem.withPreMedia}
                />
                {(!!orderItem?.sandboxItem.specificRequest ||
                  !!orderItem?.sandboxItem.preMediaFiles?.length) && (
                  <ExpressionOfNeed
                    description={orderItem?.sandboxItem.specificRequest || ""}
                    files={orderItem?.sandboxItem.preMediaFiles}
                    isDefaultCollapsed
                  />
                )}
                <TechnicalSpecifications
                  fields={fields || []}
                  orderItem={orderItem}
                  technicalOptions={technicalSpecifications || []}
                  handleQualifyDrawerOpen={handleQualifyDrawerOpen}
                />
              </VStack>
            </Box>

            <DesiredDeliveryDateBloc
              deliveryDate={orderItem.sandboxItem.deliveryDate}
            />
            <DeliveryInformationsBloc orderItem={orderItem} />
            <ClientReferenceBloc
              orderItem={orderItem}
              isUserCanEditOrder={isUserCanEditOrder}
            />
          </VStack>
        </Box>

        <Box
          w="30%"
          bgColor="elevation.first"
          py="sp-xs"
          px="sp-md"
          rounded="br-xs"
          h="max-content"
        >
          {!!orderItem && commentableOrderLog?.id && (
            <CommentsList
              isUserCanEditOrder={isUserCanEditOrder}
              orderItemId={orderItem.id}
              comments={commentableOrderLog?.comments || []}
              orderLogId={commentableOrderLog?.id}
              orderLog={commentableOrderLog}
              commentAreaRef={commentAreaRef}
            />
          )}
        </Box>
      </HStack>
    </Box>
  );
};
